import { Spacer } from '@kizen/kds/Spacer';
import { HorizontalTile, Panel } from '@kizen/kds/Tile';
import { HeaderText, LinkText, Typography } from '@kizen/kds/Typography';
import Switch from '__components/Kizen/Switch';
import { IntegrationModal } from './Modal';
import useModal from '__components/Modals/useModal';
import { Button } from '@kizen/kds/Button';
import { InstalledPlugin, MarketplacePlugin } from 'pages/Marketplace/types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@kizen/kds/Icon';
import {
  PanelFooter,
  PanelSizer,
  Thumbnail,
  ThumbnailSizer,
} from 'pages/Marketplace/styles';
import { useHistory } from 'react-router-dom';
import { ChangeEvent, useCallback, useMemo } from 'react';
import { WarningModal } from './WarningModal';
import IntercomLogo from '../../assets/intercom-square.svg';
import UserpilotLogo from '../../assets/userpilot-logo.png';
import OpenAILogo from '../../assets/openai.svg';
import MistralLogo from '../../assets/mistral.svg';
import AnthropicLogo from '../../assets/anthropic.svg';
import GeminiLogo from '../../assets/gemini.svg';
import KizenAILogo from '../../assets/kizen-ai.svg';

interface TileProps {
  onChangeEnabled?: (
    pluginApiName: string,
    enabled: boolean
  ) => Promise<unknown>;
  onChangeConfig?: (
    pluginApiName: string,
    config: Record<string, any>
  ) => Promise<unknown>;
  plugin: MarketplacePlugin;
  installation?: InstalledPlugin;
  overrideCheckedState?: boolean;
  hasBusinessPluginApp: boolean;
  currentConfig?: Record<string, any>;
  isAdmin: boolean;
  qa?: Record<string, any>;
}
const llmProviders = ['anthropic', 'open_ai', 'mistral', 'gemini'];
const pluginAppApiNames = ['kizen_ai'];

const llmProviderLogos: Record<string, any> = {
  anthropic: AnthropicLogo,
  open_ai: OpenAILogo,
  mistral: MistralLogo,
  gemini: GeminiLogo,
};

const pluginAppLogos: Record<string, any> = {
  kizen_ai: KizenAILogo,
};

const getFilePath = (path: string) => {
  if (path.startsWith('/') || path.startsWith('data:')) {
    return path;
  }

  return `${import.meta.env.VITE_API_BASE_PATH}/files/${path}/download`;
};

export const Tile = (props: TileProps) => {
  const {
    onChangeEnabled,
    onChangeConfig,
    plugin: pluginProp,
    installation,
    overrideCheckedState,
    hasBusinessPluginApp,
    currentConfig,
    isAdmin,
    qa,
  } = props;

  const { t } = useTranslation();
  const history = useHistory();

  const isEnabled = Boolean(installation);

  const [integrationModalProps, , integrationModal] = useModal({
    handleSubmit: async (config: Record<string, any>) => {
      await onChangeConfig?.(plugin.api_name, config);
    },
  });

  const [overrideModalProps, , overrideModal] = useModal({
    handleSubmit: async () => {
      await onChangeEnabled?.(plugin.api_name, true);
      return integrationModal.show();
    },
  });

  const plugin: MarketplacePlugin & {
    onChangeState?: (ev: ChangeEvent<HTMLInputElement>) => Promise<unknown>;
  } = useMemo(() => {
    // Intercom is a special plugin that we handle manually. Because we use Intercom at Kizen,
    // there is a special process to allowing customers to "override" our Intercom and swap it out
    // for their own. A plugin_app needs to exist with api_name `intercom`, but it doesn't have
    // any scripts or plugin code associated with it.
    if (pluginProp?.api_name === 'intercom') {
      return {
        ...pluginProp,
        thumbnail: IntercomLogo,
        onChangeState: (ev) => {
          if (ev.target.checked) {
            return overrideModal.show();
          }

          return onChangeEnabled?.(plugin.api_name, false);
        },
      };
    } else if (pluginProp?.api_name === 'userpilot') {
      return {
        ...pluginProp,
        thumbnail: UserpilotLogo,
        onChangeState: (ev) => {
          if (ev.target.checked) {
            return overrideModal.show();
          }

          return onChangeEnabled?.(plugin.api_name, false);
        },
      };
    } else if (llmProviders.includes(pluginProp?.api_name)) {
      return {
        ...pluginProp,
        thumbnail: llmProviderLogos[pluginProp.api_name],
      };
    } else if (pluginAppApiNames.includes(pluginProp?.api_name)) {
      return {
        ...pluginProp,
        thumbnail: pluginAppLogos[pluginProp.api_name],
      };
    }

    return pluginProp;
  }, [pluginProp, onChangeEnabled, overrideModal]);

  const editClickHandler = useCallback(() => {
    if (typeof plugin.editConfigurationOverride === 'function') {
      return plugin.editConfigurationOverride();
    } else if (typeof plugin.editConfigurationOverride === 'string') {
      return history.push(plugin.editConfigurationOverride);
    }

    return integrationModal.show();
  }, [history, integrationModal, plugin]);

  return (
    <>
      <HorizontalTile
        qa={qa}
        center={
          <div className="w-full">
            <Panel expand>
              <PanelSizer>
                {plugin.thumbnail ? (
                  <>
                    <ThumbnailSizer>
                      <Thumbnail src={getFilePath(plugin.thumbnail)} />
                    </ThumbnailSizer>
                    <Spacer size={20} mode="horizontal" />
                  </>
                ) : null}

                <div className="flex items-center justify-start kds">
                  <Switch
                    checked={
                      plugin.forceEnable
                        ? true
                        : overrideCheckedState ?? isEnabled
                    }
                    onChange={(ev) => {
                      if (plugin.forceEnable) {
                        return;
                      }

                      if (plugin.onChangeState) {
                        return plugin.onChangeState(ev);
                      }

                      return onChangeEnabled?.(
                        plugin.api_name,
                        ev.target.checked
                      );
                    }}
                    removeMargin
                    disabled={typeof overrideCheckedState === 'boolean'}
                    data-qa="toggle-plugin"
                  />
                  <Spacer size={10} mode="vertical" />
                  <HeaderText size="xl" weight="semibold">
                    {plugin.name}
                  </HeaderText>
                  {plugin.description ? (
                    <>
                      <Spacer size={10} mode="vertical" />
                      <Icon
                        tooltipEnabled
                        tooltipDescriptionOverride={plugin.description}
                        icon="message-info-circle"
                        size="sm"
                      />
                    </>
                  ) : null}
                </div>
                {plugin.external_link ? (
                  <>
                    <Spacer size={15} mode="horizontal" />
                    <LinkText
                      href={plugin.external_link}
                      target="_blank"
                      truncate
                    >
                      {plugin.external_link}
                    </LinkText>
                  </>
                ) : null}
                <PanelFooter>
                  <div className="flex w-full">
                    {(hasBusinessPluginApp ||
                      plugin.editConfigurationOverride) &&
                    isAdmin ? (
                      <div className="mr-auto">
                        <Button
                          onClick={editClickHandler}
                          variant="text"
                          size="xs"
                          color="secondary"
                        >
                          {t('Edit Configuration')}
                        </Button>
                      </div>
                    ) : null}
                    {hasBusinessPluginApp &&
                    plugin.base_config?.secrets?.length &&
                    isAdmin ? (
                      <a
                        className="ml-auto"
                        href="/settings/manage-connections"
                      >
                        <Button variant="text" size="xs" color="secondary">
                          {t('Manage Secrets')}
                        </Button>
                      </a>
                    ) : null}
                  </div>
                  <Spacer size={15} mode="horizontal" />

                  {plugin.managed_by_business?.reply_to_email ? (
                    <Typography
                      color="gray-700"
                      variant="link"
                      href={`mailto:${plugin.managed_by_business.reply_to_email}?subject=${encodeURIComponent(`Kizen Plugin Support for ${plugin.name}`)}`}
                    >
                      {t('Support Contact')}:{' '}
                      {plugin.managed_by_business.reply_to_email}
                    </Typography>
                  ) : null}
                </PanelFooter>
              </PanelSizer>
            </Panel>
          </div>
        }
      />
      <IntegrationModal
        {...integrationModalProps}
        config={currentConfig ?? {}}
        plugin={plugin}
      />
      <WarningModal
        {...overrideModalProps}
        config={currentConfig ?? {}}
        plugin={plugin}
      />
    </>
  );
};
